import React from 'react';
import { ImageWithFallback } from '../../utils/imageFallback';
import LazyOptimizedImage from './LazyOptimizedImage';
import { isBunnyAvailable } from '../../utils/bunnyImageOptimizer';

interface OptimizedImageWithFallbackProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  fallbackSrc?: string;
  onLoad?: () => void;
  onError?: () => void;
  // Bunny optimization options
  useBunnyOptimization?: boolean;
  bunnyPreset?: 'thumbnail' | 'avatar' | 'hero' | 'mobile' | 'gallery' | 'square';
  bunnyPresetSize?: number;
  quality?: number;
}

/**
 * Enhanced ImageWithFallback component that supports Bunny CDN optimization
 * Falls back to regular ImageWithFallback when Bunny optimization is disabled or unavailable
 */
const OptimizedImageWithFallback: React.FC<OptimizedImageWithFallbackProps> = ({
  src,
  alt,
  className,
  style,
  fallbackSrc,
  onLoad,
  onError,
  useBunnyOptimization = false,
  bunnyPreset = 'thumbnail',
  bunnyPresetSize,
  quality = 85,
}) => {
  // Use Bunny optimization if enabled and available
  if (useBunnyOptimization && isBunnyAvailable()) {
    return (
      <LazyOptimizedImage
        src={src}
        alt={alt}
        className={className}
        style={style}
        fallbackSrc={fallbackSrc}
        onLoad={onLoad}
        onError={onError}
        useBunnyOptimization={true}
        bunnyPreset={bunnyPreset}
        bunnyPresetSize={bunnyPresetSize}
        quality={quality}
        lazy={false} // For small icons, don't lazy load
        size="small"
      />
    );
  }

  // Fall back to regular ImageWithFallback
  return (
    <ImageWithFallback
      src={src}
      alt={alt}
      className={className}
      style={style}
      fallbackSrc={fallbackSrc}
      onLoad={onLoad}
      onError={onError}
    />
  );
};

export default OptimizedImageWithFallback;
